import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Star, Check, Crown, Zap } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { useAuth } from '@/contexts/AuthContext';
import ConsumerLayout from '@/components/ConsumerLayout';
import api from '@/lib/api';
import { toast } from 'sonner';
import { useState } from 'react';

const GoldClub = () => {
  const navigate = useNavigate();
  const { isAuthenticated, isGold } = useAuth();
  const [loading, setLoading] = useState(false);

  const benefits = [
    { icon: Star, text: 'Access to Gold Exclusive events' },
    { icon: Zap, text: 'Priority booking for all events' },
    { icon: Crown, text: 'Special pricing on tickets and vouchers' },
    { icon: Check, text: 'VIP treatment at partner venues' },
    { icon: Check, text: 'Early access to new events' },
    { icon: Check, text: 'Exclusive member-only experiences' }
  ];

  const handleSubscribe = async () => {
    if (!isAuthenticated) {
      toast.error('Please sign in to join Gold Club');
      navigate('/auth');
      return;
    }

    if (isGold) {
      toast.info('You are already a Gold member!');
      return;
    }

    setLoading(true);
    try {
      const response = await api.post('/payments/checkout', {
        item_type: 'gold_membership',
        item_id: 'gold_club',
        quantity: 1,
        origin_url: window.location.origin
      });
      
      window.location.href = response.data.url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process subscription');
      setLoading(false);
    }
  };

  return (
    <ConsumerLayout>

      <div className="relative overflow-hidden py-20 bg-gradient-to-br from-[#0A0A0A] via-[#1a1a1a] to-[#0A0A0A] text-white">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAxMCAwIEwgMCAwIDAgMTAiIGZpbGw9Im5vbmUiIHN0cm9rZT0iIzMzMyIgc3Ryb2tlLXdpZHRoPSIxIi8+PC9wYXR0ZXJuPjwvZGVmcz48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSJ1cmwoI2dyaWQpIi8+PC9zdmc+')] opacity-50" />
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            data-testid="gold-club-hero"
          >
            <div className="inline-flex items-center gap-2 bg-[#D4AF37]/20 border border-[#D4AF37] px-4 py-2 rounded-full mb-6">
              <Star className="w-4 h-4 text-[#D4AF37] fill-current" />
              <span className="text-[#D4AF37] font-bold text-sm">EXCLUSIVE MEMBERSHIP</span>
            </div>
            <h1 className="text-5xl lg:text-7xl font-bold mb-6">
              EVENT★GOERS
              <br />
              <span className="bg-gradient-to-r from-[#D4AF37] to-[#FFD700] bg-clip-text text-transparent">
                Gold Club
              </span>
            </h1>
            <p className="text-xl text-white/80 mb-12 max-w-2xl mx-auto">
              Not just membership — a status symbol. Gain exclusive access to the best of RiNo's cultural scene.
            </p>
          </motion.div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl font-bold text-foreground mb-8">
              Membership Benefits
            </h2>
            <div className="space-y-4">
              {benefits.map((benefit, index) => {
                const Icon = benefit.icon;
                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className="flex items-center gap-4 p-4 rounded-lg border bg-card"
                  >
                    <div className="flex-shrink-0 w-10 h-10 rounded-full bg-[#D4AF37]/20 flex items-center justify-center">
                      <Icon className="w-5 h-5 text-[#D4AF37]" />
                    </div>
                    <p className="text-lg text-foreground">{benefit.text}</p>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="sticky top-24"
          >
            <Card className="border-2 border-[#D4AF37]" data-testid="gold-club-pricing-card">
              <CardContent className="p-8">
                <div className="text-center mb-8">
                  <div className="inline-flex items-center gap-2 bg-[#D4AF37]/20 border border-[#D4AF37] px-4 py-2 rounded-full mb-4">
                    <Crown className="w-4 h-4 text-[#D4AF37]" />
                    <span className="text-[#D4AF37] font-bold text-sm">ANNUAL MEMBERSHIP</span>
                  </div>
                  <div className="mb-2">
                    <span className="text-6xl font-bold text-foreground">$1,000</span>
                    <span className="text-xl text-muted-foreground">/year</span>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Less than $9 per month
                  </p>
                </div>

                {isGold ? (
                  <div className="text-center py-8">
                    <div className="inline-flex items-center gap-2 bg-[#D4AF37]/20 border border-[#D4AF37] px-6 py-3 rounded-full">
                      <Star className="w-5 h-5 text-[#D4AF37] fill-current" />
                      <span className="text-[#D4AF37] font-bold">You're a Gold Member!</span>
                    </div>
                  </div>
                ) : (
                  <Button
                    className="w-full h-14 bg-[#D4AF37] hover:bg-[#B5952F] text-black font-bold text-lg"
                    onClick={handleSubscribe}
                    disabled={loading}
                    data-testid="subscribe-gold-btn"
                  >
                    {loading ? 'Processing...' : 'Join Gold Club'}
                  </Button>
                )}

                <div className="mt-6 pt-6 border-t space-y-2 text-sm text-muted-foreground text-center">
                  <p>✓ Instant access after payment</p>
                  <p>✓ Cancel anytime</p>
                  <p>✓ Money-back guarantee</p>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </div>

    </ConsumerLayout>
  );
};

export default GoldClub;
